// Import necessary React hooks and components
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
// Import necessary Material-UI components
import { DataGrid } from "@mui/x-data-grid";
import { Button, Stack } from "@mui/material";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import Box from "@mui/system/Box";
// Import custom validation, functions, hooks and components
import {
  useAuthInfo,
  authErrorRedirect,
  CustomMUITableContainer,
  CustomTableHeaderWithButton,
  createSearchColumns,
  ExportCsvButton,
  downloadCsvViaExport,
  buildCsvColumns,
} from "../../common";
import { BulkApprovalDialog } from "./";
import {
  ApprovalItemsList,
  bulkApprovalColumnConfig,
  onBulkApprovalRowSelectionHandler,
} from "./functions";

/**
 * AllItemsBulkApproval component definition for the bulk approval page
 */
export function AllItemsBulkApproval() {
  // Server-side pagination: the grid asks for a page, the backend returns just
  // that page plus the total row count.
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [rowCount, setRowCount] = useState(0);
  // Controlled selection so it can be cleared when the query changes (selection
  // is scoped to the loaded page — see the fetch effect below).
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  // State for handling API errors
  const [errors, setErrors] = useState();
  // Navigation utility from React Router
  const history = useNavigate();
  // Custom hook to retrieve authenticated user information
  const decoded = useAuthInfo();
  // State for storing fetched item list data
  const [itemList, setItemList] = useState();

  // State for button loading state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // State for storing API endpoint based on user role
  const [requestURN, setRequestURN] = useState();
  // State for selected rows to update
  const [selectedRowsData, setSelectedRowsData] = useState([]);
  // State for summary of selected rows
  const [selectedRowsSummaryData, setSelectedRowsSummaryData] = useState([]);

  // Per-column server-side search: { [field]: value }. `value` is a string for
  // text columns and { from, to } for date columns. Empty entries are removed so
  // the query param only carries active filters.
  const [searchModel, setSearchModel] = useState({});
  const searchKey = JSON.stringify(searchModel);
  // Bumped to visually reset the header inputs (clear-all).
  const [filterResetKey, setFilterResetKey] = useState(0);
  const activeFilterCount = Object.keys(searchModel).length;

  // Server-side single-column sort: [] or [{ field, sort: "asc" | "desc" }].
  const [sortModel, setSortModel] = useState([]);
  const sortKey = JSON.stringify(sortModel);

  // Cycles a column's sort: none → asc → desc → none, and restarts paging.
  const handleSortChange = useCallback((field) => {
    setSortModel((prev) => {
      const current = prev[0];
      if (!current || current.field !== field) return [{ field, sort: "asc" }];
      if (current.sort === "asc") return [{ field, sort: "desc" }];
      return [];
    });
    setPaginationModel((model) => ({ ...model, page: 0 }));
  }, []);

  // Applies a single column's search value, dropping it when cleared, and
  // restarts paging from the first page so results aren't shown on a stale page.
  const handleSearchChange = useCallback((field, value) => {
    setSearchModel((prev) => {
      const next = { ...prev };
      const isEmpty =
        value == null ||
        value === "" ||
        (typeof value === "object" && !value.from && !value.to);
      if (isEmpty) {
        delete next[field];
      } else {
        next[field] = value;
      }
      return next;
    });
    setPaginationModel((model) => ({ ...model, page: 0 }));
  }, []);

  // Clears every active column search at once and remounts the header inputs.
  const handleClearAllSearch = useCallback(() => {
    setSearchModel({});
    setFilterResetKey((k) => k + 1);
    setPaginationModel((model) => ({ ...model, page: 0 }));
  }, []);

  // Always-fresh reference to the current search model, so a memoized header can
  // seed its control from the latest value without rebuilding the columns on
  // every keystroke.
  const searchModelRef = useRef(searchModel);
  searchModelRef.current = searchModel;

  // Column definitions built by the shared helper (per-column search headers).
  // Memoized on filterResetKey so the search inputs are only remounted on
  // clear-all, not on every keystroke.
  const columnDefinition = useMemo(
    () =>
      createSearchColumns({
        columnConfig: bulkApprovalColumnConfig,
        searchModelRef,
        onSearchChange: handleSearchChange,
        filterResetKey,
        sortModel,
        onSortChange: handleSortChange,
      }),
    [filterResetKey, handleSearchChange, sortModel, handleSortChange]
  );

  // State for managing bulk approval dialog visibility
  const [openBulkApprovalDialog, setOpenBulkApprovalDialog] = useState(false);
  const handleOpenBulkApprovalDialog = () => {
    setOpenBulkApprovalDialog(true);
  };

  // useEffect hook to fetch approval items based on user role, page, and search
  useEffect(() => {
    // Determine API endpoint based on user role
    switch (decoded.role) {
      case "chef":
        setRequestURN(`userApprovalItems/chef/${decoded.email}`);
        break;
      case "executiveChef":
        setRequestURN(`userApprovalItems/executiveChef/${decoded.email}`);
        break;
      case "hod":
        setRequestURN(`userApprovalItems/hod/${decoded.department}`);
        break;
      default:
        console.log("There was an error in All Requests Page Axios Call");
    }

    // Function to fetch the current page of approval items from backend
    async function getApprovalItemList() {
      // Undefined rows put the grid into its loading state while the page loads.
      setItemList(undefined);
      try {
        const hasSearch = Object.keys(searchModel).length > 0;
        const hasSort = sortModel.length > 0;
        const { data } = await ApprovalItemsList.get({
          requestURN,
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          // Only send the search param when there are active filters.
          search: hasSearch ? JSON.stringify(searchModel) : undefined,
          // Only send the sort param when a column sort is active.
          sort: hasSort
            ? JSON.stringify({
                field: sortModel[0].field,
                dir: sortModel[0].sort,
              })
            : undefined,
        });
        const { rows = [], total = 0 } = data ?? {};
        // Set fetched items + total row count to state.
        setItemList(rows);
        setRowCount(total);
      } catch (error) {
        console.log("error in axios approval items list api react");
        setItemList([]);
        setRowCount(0);
        // Clear errors
        setErrors({});
        // Set errors received from server response
        setErrors(error.response.data);
        // Redirect on authentication error
        authErrorRedirect(error.response.data, history);
      }
    }

    // Any list/page/search change invalidates the prior selection (it is scoped
    // to the loaded page — only loaded rows resolve into the approval payload).
    setRowSelectionModel([]);
    setSelectedRowsData([]);
    setSelectedRowsSummaryData([]);

    // Fetch items if requestURN is set
    requestURN && getApprovalItemList();
    // searchModel / sortModel are tracked via searchKey / sortKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    decoded.role,
    decoded.email,
    requestURN,
    decoded.department,
    history,
    paginationModel.page,
    paginationModel.pageSize,
    searchKey,
    sortKey,
  ]);

  // Downloads the FULL result set for the current search + sort as a CSV
  // (built + streamed by the backend), reusing the same endpoint + the visible
  // column list.
  function handleExport() {
    // requestURN is set only once the role resolves; the server derives the
    // approver scope from req.user, so no identifier is sent.
    if (!requestURN) return Promise.resolve();
    return downloadCsvViaExport({
      list: "bulkApproval",
      columns: buildCsvColumns(bulkApprovalColumnConfig),
      search: searchModel,
      sort: sortModel.length
        ? { field: sortModel[0].field, dir: sortModel[0].sort }
        : undefined,
      filename: `bulk-approval_${new Date().toISOString().slice(0, 10)}.csv`,
    });
  }

  // Function to handle submission of bulk approvals
  const handleSubmit = async (approval) => {
    setIsSubmitting(true);
    try {
      await ApprovalItemsList.post({
        approval,
        selectedRowsData,
        email: decoded.email,
        role: decoded.role,
      });
      // Refresh page after submission
      window.location.reload(true);
    } catch (error) {
      console.log(error);
    }
  };

  // Log errors to console if any
  errors && console.log(errors);

  return (
    <span>
      {/* Custom table header with button to trigger bulk approval dialog */}
      <CustomMUITableContainer
        children={
          <Box>
            <CustomTableHeaderWithButton
              title="Bulk Approval"
              description="Click on a row to edit the corresponding item's sample request"
              buttonText=" Approve/Reject"
              buttonOnClick={handleOpenBulkApprovalDialog}
            />
            {/* Clear-all-filters + Download CSV controls. */}
            <Stack
              direction="row"
              spacing={2}
              sx={{ marginLeft: "20px", paddingBottom: "10px" }}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterAltOffIcon />}
                onClick={handleClearAllSearch}
                disabled={!activeFilterCount}
                sx={{ borderRadius: "10px", whiteSpace: "nowrap" }}
              >
                {activeFilterCount
                  ? `Clear Filters (${activeFilterCount})`
                  : "Clear Filters"}
              </Button>
              <ExportCsvButton
                onExport={handleExport}
                rowCount={rowCount}
                loading={!itemList}
              />
            </Stack>
            <Box
              sx={{
                "& .detailsColumn": {
                  display: "flex !important",
                  alignItems: "flex-start !important",
                },
              }}
            >
              {/* DataGrid component to display items */}
              <DataGrid
                rows={itemList ?? []}
                loading={!itemList}
                autoHeight={true}
                columns={columnDefinition}
                // Each row is one supplier entry; qualityReportsID is unique per
                // row (and always present), so it is the stable row id.
                getRowId={(row) => row.qualityReportsID}
                // Server-side pagination: the grid renders the loaded page and
                // reports page changes back; the backend returns rows + total.
                paginationMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                getRowHeight={() => "auto"}
                // Taller header row to fit the embedded search controls.
                columnHeaderHeight={96}
                disableRowSelectionOnClick
                // Built-in (client-side) filtering / column menu are replaced by
                // the server-driven per-column search embedded in the headers.
                disableColumnFilter
                disableColumnMenu
                // Customize cell class for Details column
                getCellClassName={(params) => {
                  if (params.field === "Details") {
                    return "detailsColumn";
                  }
                }}
                checkboxSelection
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={(ids) => {
                  setRowSelectionModel(ids);
                  onBulkApprovalRowSelectionHandler(
                    ids,
                    itemList,
                    setSelectedRowsData,
                    setSelectedRowsSummaryData
                  );
                }}
                sx={{
                  border: 0,
                  "& .MuiDataGrid-columnHeaderTitleContainer": {
                    alignItems: "flex-start",
                  },
                  // Let the custom header content fill the column so the embedded
                  // controls can stretch full width.
                  "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                    flex: 1,
                    width: "100%",
                    overflow: "visible",
                  },
                }}
              />
            </Box>
          </Box>
        }
      />
      {/* Bulk approval dialog component */}
      <BulkApprovalDialog
        open={openBulkApprovalDialog}
        setOpen={setOpenBulkApprovalDialog}
        handleOpen={handleOpenBulkApprovalDialog}
        selectedRowsData={selectedRowsData}
        selectedRowsSummary={selectedRowsSummaryData}
        handleSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />
    </span>
  );
}
